import { html, LitElement } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlButton, ButtonEnums } from '../button/index.ts';
import { enumAttributeConverter, Sizes } from '../enum-types.ts';
import { AtlantisEvent } from '../helpers.ts';
import * as DropdownEnums from './dropdown.props.ts';
import { AtlDropdownProps, DropdownMenuAlignment } from './dropdown.props.ts';

import { classMap } from 'lit/directives/class-map.js';
import { BootstrapDropdown } from '../BootstrapDrowndown.ts';
import { AtlButtonTemplate } from '../button/button.component.ts';
import { DropDownItemClickEvent } from './dropdown.events.ts';
import * as TagNames from './dropdown.tagnames.ts';

@customElement(TagNames.Dropdown)
export class AtlDropdown extends LitElement implements AtlDropdownProps {
   declare toggleLabel: string;
   declare toggleIconName: string;
   declare toggleAriaLabel: string;
   declare toggleReverseOrder?: boolean;
   declare toggleClasses?: string;
   declare toggleIconClasses?: string;
   declare toggleAs: DropdownEnums.DropdownToggleAs;
   declare variant: DropdownEnums.DropdownVariant;
   declare menuAlignment?: DropdownMenuAlignment;
   declare size: Sizes;
   declare disabled: boolean;
   declare autoClose?: DropdownEnums.DropdownAutoClose;
   declare _isEmpty?: boolean;

   static styles = [...getGlobalStyleSheets()];

   static properties = {
      toggleLabel: { attribute: 'toggle-label', type: String },
      toggleIconName: { attribute: 'toggle-icon-name', type: String },
      toggleAriaLabel: { attribute: 'toggle-aria-label', type: String },
      toggleReverseOrder: { attribute: 'toggle-reverse-order', type: Boolean },
      toggleClasses: { attribute: 'toggle-classes', type: String },
      toggleIconClasses: { attribute: 'toggle-icon-classes', type: String },
      toggleAs: {
         attribute: 'toggle-as',
         type: String,
         converter: enumAttributeConverter(DropdownEnums.DropdownToggleAs),
      },
      variant: {
         type: String,
         converter: enumAttributeConverter(DropdownEnums.DropdownVariant),
      },
      size: { type: String, converter: enumAttributeConverter(Sizes) },
      disabled: { type: Boolean },
      autoClose: { type: String, attribute: 'auto-close' },
      static: { type: Boolean },
      menuAlignment: {
         type: String,
         attribute: 'menu-alignment',
         converter: enumAttributeConverter(DropdownMenuAlignment),
      },
      _isEmpty: { type: Boolean, state: true },
   };

   constructor() {
      super();
      this.toggleAs = DropdownEnums.DropdownToggleAs.Button;
      this.toggleIconName = 'chevron-down';
   }

   override connectedCallback() {
      super.connectedCallback();
      window.addEventListener('click', this._handleWindowClick);
      window.addEventListener(
         DropDownItemClickEvent.EVENT_NAME,
         this._handleDropdownItemClick
      );
   }

   override disconnectedCallback() {
      window.removeEventListener(
         DropDownItemClickEvent.EVENT_NAME,
         this._handleDropdownItemClick
      );
      window.removeEventListener('click', this._handleWindowClick);
      super.disconnectedCallback();
   }

   private _handleWindowClick = (event: MouseEvent) => {
      switch (this.autoClose?.toLowerCase()) {
         case DropdownEnums.DropdownAutoClose.Inside:
         case DropdownEnums.DropdownAutoClose.False:
            return;
      }

      if (!this._dropDownList || !this._dropDownList.classList.contains('show'))
         return;

      const path = event.composedPath();

      const isButtonClicked =
         this._dropDownButton && path.includes(this._dropDownButton);
      if (isButtonClicked) return;

      const isClickInside =
         this._dropDownList && path.includes(this._dropDownList);
      if (!isClickInside) {
         this._bootStrapDropdown?.hide();
      }
   };

   private _handleDropdownItemClick = () => {
      switch (this.autoClose?.toLowerCase()) {
         case DropdownEnums.DropdownAutoClose.Outside:
         case DropdownEnums.DropdownAutoClose.False:
            return;
      }
      this._bootStrapDropdown?.hide();
   };

   @query('button.btn, button.chip')
   _dropDownButton?: AtlButton;

   @query('div.dropdown-menu')
   _dropDownList?: HTMLDivElement;

   private _bootStrapDropdown?: BootstrapDropdown;

   private get _buttonVariant() {
      switch (this.variant) {
         case DropdownEnums.DropdownVariant.Secondary:
            return ButtonEnums.ButtonVariant.Secondary;
         case DropdownEnums.DropdownVariant.Action:
            return ButtonEnums.ButtonVariant.Action;
         case DropdownEnums.DropdownVariant.Destructive:
            return ButtonEnums.ButtonVariant.Destructive;
         case DropdownEnums.DropdownVariant.DestructiveAction:
            return ButtonEnums.ButtonVariant.DestructiveAction;
         case DropdownEnums.DropdownVariant.Light:
            return ButtonEnums.ButtonVariant.Light;
         default:
            return ButtonEnums.ButtonVariant.Cta;
      }
   }

   private get _disabled() {
      return this.disabled || this._isEmpty;
   }

   private _handleSlotChange = (
      event: AtlantisEvent<Event, HTMLSlotElement>
   ) => {
      const slot = event.target;
      const assignedNodes = slot
         .assignedNodes({ flatten: true })
         .filter((node) => node.nodeType === Node.ELEMENT_NODE)
         .filter(
            (node) =>
               (node as HTMLElement).tagName ===
               TagNames.DropdownItem.toUpperCase()
         );

      this._isEmpty = assignedNodes.length === 0;

      if (this._dropDownButton) {
         this._bootStrapDropdown = bootstrap.Dropdown.getOrCreateInstance(
            this._dropDownButton
         );
      }
   };

   render() {
      return html`
         <div class="dropdown" part="dropdown">
            ${AtlButtonTemplate({
               variant: this._buttonVariant,
               buttonAriaSettings: {
                  ariaLabel: this.toggleAriaLabel ?? this.toggleLabel,
               },
               dataBsToggle: ButtonEnums.ButtonDataBsToggle.dropdown,
               iconName: this.toggleIconName,
               size: this.size,
               buttonClasses: this.toggleClasses,
               iconClassName: this.toggleIconClasses,
               reverseOrder: this.toggleReverseOrder !== true,
               asChip: this.toggleAs === DropdownEnums.DropdownToggleAs.Chip,
               disabled: this._disabled,
               onClick: this._handleToggle,
               innerContent: html`<slot name="dropdownInnerContent"
                  >${this.toggleLabel}</slot
               >`,
            })}
            <div
               class=${classMap({
                  'dropdown-menu': true,
                  'dropdown-menu-end':
                     this.menuAlignment === DropdownMenuAlignment.Right,
               })}
            >
               <slot @slotchange=${this._handleSlotChange}></slot>
            </div>
         </div>
      `;
   }

   private _handleToggle = () => {
      this._bootStrapDropdown?.toggle();
   };
}
